<template>
  <div id="app" @keydown.shift="handlePrefillFormKey">
    <!-- NOTE: Hidden in Percy since it wrongly triggers visual diffs -->
    <vue-progress-bar class="hide-in-percy"></vue-progress-bar>

    <HeaderNav />

    <keep-alive include="Game">
      <HttpError v-if="httpError" />
      <router-view v-else class="app-content" />
    </keep-alive>
  </div>
</template>

<script>
import ahoy from 'ahoy.js';
import { mapState, mapGetters } from 'vuex';
import { configureScope } from '@sentry/browser';
import { appName, appDescription, clientOrigin } from '@/constants';

import HeaderNav from '@/components/HeaderNav';
import HttpError from '@/views/HttpError';
import prefillableDevelopmentDataMixin from '@/mixins/prefillable-development-data';
import {
  metaTitles,
  metaDescriptions,
  metaCanonicalLink,
  metaImages,
} from '@/utils/meta';

export default {
  name: 'App',

  components: {
    HeaderNav,
    HttpError,
  },

  mixins: [prefillableDevelopmentDataMixin],

  metaInfo() {
    return {
      title: appName,
      meta: [
        ...metaTitles(appName),
        ...metaDescriptions(appDescription),
        ...metaImages(
          {
            url: `${clientOrigin}/img/logo-facebook.png`,
            metadata: {
              width: 1200,
              height: 627,
              mimeType: 'image/png',
            },
          },
          `${appName} logo`
        ),
      ],
      link: [...metaCanonicalLink.call(this)],
    };
  },

  computed: {
    ...mapState(['httpError', 'showPrefillNotice']),
    ...mapGetters(['settings', 'currentUser']),
  },

  watch: {
    currentUser(value, previous) {
      if (!value) return;
      if (previous && previous.id === value.id) return;

      const userData = {
        id: value.id,
        email: value.email,
        role: value.role,
      };

      configureScope((scope) => scope.setUser(userData));
    },
  },

  mounted() {
    ahoy.trackAll();
  },
};
</script>

<style lang="scss">
@import 'node_modules/modern-normalize/modern-normalize';

@import '@/styles/mixins/form-control';
@import '@/styles/mixins/button';

html {
  box-sizing: border-box;
}

*,
*:before,
*:after {
  box-sizing: inherit;
}

body,
#app {
  min-height: 100vh;
}

html,
body,
#app {
  display: flex;
  flex-direction: column;
}

html {
  background-color: map-get($colors, 'header-background');
}

body {
  background: radial-gradient(
    circle,
    rgba(26, 98, 154, 1) 0%,
    rgba(17, 75, 120, 1) 100%
  );
}

.app-content {
  flex-grow: 1;

  padding-bottom: map-get($app, 'spacing');
}

html,
body,
fieldset {
  margin: 0;
  padding: 0;
}

body {
  // System font stack. See https://bitsofco.de/the-new-system-font-stack
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial,
    sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  font-size: 16px;
  color: map-get($colors, 'white');
  line-height: 1.45;
}

// See https://stackoverflow.com/a/6140504
h1 {
  font-size: 2em;
}

h2 {
  font-size: 1.5em;
}

h3 {
  font-size: 1.17em;
}

h4 {
  font-size: 1.12em;
}

h5 {
  font-size: 0.83em;
}

h6 {
  font-size: 0.75em;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  margin-bottom: 0;
}

fieldset {
  border: 0;

  &[disabled] {
    opacity: map-get($app, 'disabled-opacity');
  }
}

textarea,
select,
#{map-get($form-input, 'text')},
#{map-get($form-input, 'number')},
#{map-get($form-input, 'dates')} {
  @include form-control;
}

button,
#{map-get($form-input, 'buttons')} {
  @include button;
}

b {
  font-weight: map-get($font-weights, 'bold-2x');
}

a {
  color: map-get($colors, 'links');
  text-decoration: underline;

  &.router-link-active,
  &.router-link-exact-active {
    color: map-get($colors, 'links-active');
  }
}

[role='button'] {
  cursor: pointer;
}
</style>
